import type * as React from "react";

import { cn } from "@/lib/utils";

/**
 * Base Skeleton component with shimmer animation.
 * Use this as a building block for loading placeholders.
 */
function Skeleton({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="skeleton"
      className={cn(
        "rounded-md bg-zinc-100",
        "bg-[length:200%_100%]",
        "bg-gradient-to-r from-zinc-100 via-white to-zinc-100",
        "animate-shimmer",
        className,
      )}
      {...props}
    />
  );
}

/**
 * Skeleton card matching the event/club card layout.
 * Displays image placeholder, title, and subtitle.
 */
function SkeletonCard({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="skeleton-card"
      className={cn("flex flex-col gap-3 overflow-hidden", className)}
      {...props}
    >
      {/* Image placeholder */}
      <Skeleton className="aspect-[16/9] w-full rounded-md" />

      {/* Text content */}
      <div className="flex flex-col gap-2">
        {/* Title */}
        <Skeleton className="h-6 w-full rounded-md" />
        {/* Subtitle */}
        <Skeleton className="h-4 w-full rounded-md" />
      </div>
    </div>
  );
}

/**
 * Skeleton for a grid of cards (events/clubs list).
 * Shows 6 skeleton cards in a responsive grid layout.
 */
function SkeletonCardGrid({
  count = 6,
  className,
  ...props
}: React.ComponentProps<"div"> & { count?: number }) {
  return (
    <div
      data-slot="skeleton-card-grid"
      className={cn("grid gap-5 sm:grid-cols-2 xl:grid-cols-3", className)}
      {...props}
    >
      {Array.from({ length: count }).map((_, index) => (
        <SkeletonCard key={`skeleton-${index}`} />
      ))}
    </div>
  );
}

/**
 * Skeleton for the map container.
 * Shows a loading placeholder matching map dimensions.
 */
function SkeletonMap({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="skeleton-map"
      className={cn(
        "w-full h-full min-h-[400px] flex items-center justify-center",
        className,
      )}
      {...props}
    >
      <Skeleton className="w-full h-full rounded-xl" />
    </div>
  );
}

export { Skeleton, SkeletonCard, SkeletonCardGrid, SkeletonMap };
